/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { organizationRemoteSessionsRevoke } from "../funcs/organizationRemoteSessionsRevoke.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  RevokeOrganizationRemoteSessionRequest,
  RevokeOrganizationRemoteSessionSecurity,
} from "../models/operations/revokeorganizationremotesession.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type RevokeOrganizationRemoteSessionMutationVariables = {
  request: RevokeOrganizationRemoteSessionRequest;
  security?: RevokeOrganizationRemoteSessionSecurity | undefined;
  options?: RequestOptions;
};

export type RevokeOrganizationRemoteSessionMutationData = void;

export type RevokeOrganizationRemoteSessionMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * revokeSession organizationRemoteSessions
 *
 * @remarks
 * Revoke (soft-delete) a single remote_session in the caller's organization. Requires org:admin.
 */
export function useRevokeOrganizationRemoteSessionMutation(
  options?: MutationHookOptions<
    RevokeOrganizationRemoteSessionMutationData,
    RevokeOrganizationRemoteSessionMutationError,
    RevokeOrganizationRemoteSessionMutationVariables
  >,
): UseMutationResult<
  RevokeOrganizationRemoteSessionMutationData,
  RevokeOrganizationRemoteSessionMutationError,
  RevokeOrganizationRemoteSessionMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildRevokeOrganizationRemoteSessionMutation(client, options),
    ...options,
  });
}

export function mutationKeyRevokeOrganizationRemoteSession(): MutationKey {
  return ["@gram/client", "organizationRemoteSessions", "revoke"];
}

export function buildRevokeOrganizationRemoteSessionMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: RevokeOrganizationRemoteSessionMutationVariables,
  ) => Promise<RevokeOrganizationRemoteSessionMutationData>;
} {
  return {
    mutationKey: mutationKeyRevokeOrganizationRemoteSession(),
    mutationFn: function revokeOrganizationRemoteSessionMutationFn({
      request,
      security,
      options,
    }): Promise<RevokeOrganizationRemoteSessionMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(organizationRemoteSessionsRevoke(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
